'use strict';
/* ==========================================================================
   levels/title.js — the title screen, NOTEBOOK style. On the page's graph paper: the antique two-pan balance
   (core-local/balancekit.js, drawn in ink by code) with the video's first equation on it: two sock boxes and six
   one-ounce weights on the left; on the right, twenty weights land one by one and the beam comes level as they do
   (the tilt comes from the model: Scale.tilt(2 × 7 + 6, weights so far)). Under it "2x + 6 = 20" writes itself on.
   Jay rises in from below on the right. The title is handwritten with a highlighter swipe on its last word ("It",
   NB.title from lesson.json), ▶ Play → the map. Pure in time: everything keyed to T and the scene's entry time.
   On a phone held upright the controls move to the portrait dock (Dock.title) and the stage keeps only the art.
   Levels.title.loading() draws the loading page while the boot tier loads.
   ========================================================================== */
(function () {
  const PLAY = { x: 1400, y: 820 };
  const HANDF = NB.FONT.hand;
  const EQ = Scale.mkEq(Scale.VIDEO.s1.eq), X = Scale.solve(EQ);                 // 2x + 6 = 20, x = 7
  const LEFT = Scale.toNumber(Scale.value(EQ, X)), RIGHT = Scale.toNumber(EQ.r);  // 20 and 20

  /* the title in one or two lines, each sized to fit maxW logical px */
  function titleLines(text, maxW, max) {
    const width = (s, size) => { g.save(); g.font = `700 ${size}px ${HANDF}`; const w = g.measureText(s).width; g.restore(); return w; };
    const words = String(text).split(/\s+/).filter(Boolean);
    let lines = [words.join(' ')];
    if (width(lines[0], max) > maxW && words.length > 1) {
      let best = null;
      for (let k = 1; k < words.length; k++) {
        const a = words.slice(0, k).join(' '), b = words.slice(k).join(' '), d = Math.abs(width(a, 100) - width(b, 100));
        if (!best || d < best.d) best = { d, lines: [a, b] };
      }
      lines = best.lines;
    }
    const size = Math.min(max, ...lines.map(l => max * maxW / Math.max(1, width(l, max))));
    return { lines, size };
  }

  /* the balance: the right pan fills weight by weight; the beam follows the model's tilt (continuous in time) */
  function balance(t0) {
    const fill = E.inOutSine(P(T, t0 + 0.4, t0 + 2.2)) * RIGHT;           // weights on the right so far (fractional)
    const n = Math.floor(fill + 1e-9);
    const c = Scale.contents(EQ);
    BalanceKit.draw(580, 880, { s: 0.9, tilt: Scale.tilt(LEFT, fill), left: c.left, right: { ...c.right, weights: n }, v: EQ.v });
    NB.write(Scale.text(EQ), 580, 960, { size: 60, weight: 700, align: 'center', t0: t0 + 2.3, dur: 0.6 });
    if (T > t0 + 3.1) NB.note('x = the weight of ONE box', 580, 1024, -0.02, { t0: t0 + 3.1, size: 30, color: NB.PAL.pencil, align: 'center' });
  }

  class Title extends Scene {
    enter() { this.started = false; }
    go() {
      if (this.started) return;
      this.started = true;
      Audio.unlock();
      if (Game.MUSIC) Audio.music(Game.MUSIC, { gain: 0.3 });
      Game.replace(new Levels.map());
    }
    keyDown(k) { if (k === 'Enter' || k === ' ') this.go(); }

    draw() {
      UI.allLessons();                       // "← All lessons" (a tab; in the portrait dock a button in its bar)
      const t = this.t, t0 = this.enteredAt, L = Game.lesson || {};
      NB.ground();
      balance(t0 + 0.1);
      // Jay rises in from below on the right, under the title
      const rise = E.outBack(P(T, t0 + 0.6, t0 + 1.1), 1.2);
      Puppet.draw('jay', 1790, 1080 + (1 - rise) * 600, 450, { facing: 0, t: T });
      // the title, handwritten; the highlighter lands on its last word ("It")
      const { lines, size } = titleLines(L.title || 'New lesson', 880, 130);
      let y = lines.length > 1 ? 250 : 320;
      lines.forEach((l, i) => {
        const last = i === lines.length - 1 ? l.split(/\s+/).pop() : null;
        NB.title(l, 1400, y, { size, t0: t0 + 0.3 + i * 0.5, hi: last });
        y += size * 1.2;
      });
      NB.write(L.subtitle || '', 1400, y + 20, { font: 'ui', size: 36, color: NB.PAL.pencil, align: 'center', t0: t0 + 1.2, dur: 0.4 });

      // portrait phone: the controls are in the dock (core/dock.js Dock.title); the stage keeps only the art
      if (UI.docked()) { Dock.title({ onStart: () => this.go() }); return; }
      if (t > 1.4 && UI.button('play', '▶ Play', PLAY.x, PLAY.y, { w: 420, h: 136, size: 60, primary: true })) this.go();
      if (t > 2.4) NB.write(UI.hint('(or press Enter)', 'tap ▶ Play to start'), PLAY.x, PLAY.y + 104 + (UI.tap(136) - 136) / 2, { font: 'ui', size: UI.text(30), color: NB.PAL.pencil, align: 'center', t0: t0 + 2.4 });
    }
  }
  Title.PLAY = PLAY;                         // tools/integration.js clicks it

  /* the loading page (no assets yet): a pencil drawing a line along a ruler */
  Title.loading = function () { NB.loadingScreen('loading…'); };

  Levels.title = Title;
})();
